import React, { useState } from "react"
import { Container, Row, Col, Card, Nav } from "react-bootstrap"
import { HiOutlineRefresh } from "react-icons/hi"
import { useTranslation } from "react-i18next"

import IonIcon from "../../../components/ionIcon"
import VideoModal from "../../../components/video-modal"
import { useExampleDestination } from "../../../hooks/useDestinations"
import { mapOrder } from "../../../helpers/mapOrder"
import { getLocaleValue } from "../../../helpers/getLocaleValue"
import { GuideImage, GuideVideoThumbnail } from "../Guides/components"
import { GuideTextPreview } from "../Guides/components/text"
import type { Guide, GuideImageData, VideoData } from "../../../constants/types"

type VideoModalState = {
  display: boolean
  video: Record<string, unknown> | null
}

const ExamplesContainer: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { data: destination, isLoading } = useExampleDestination()

  const [language, setLanguage] = useState(i18n.resolvedLanguage ?? "en")
  const [selectedGuideId, setSelectedGuideId] = useState<string | null>(null)
  const [videoModal, setVideoModal] = useState<VideoModalState>({ display: false, video: null })

  const guides = destination?.guides ?? []
  const selectedGuide = guides.find(x => x.id === selectedGuideId)
  const blocks = selectedGuide?.content?.blocks ?? []
  const reorderedGuides = guides.length ? mapOrder(guides, destination?.order ?? undefined, "id") : []

  const openVideoModal = (data: Record<string, unknown>) => setVideoModal({ display: true, video: data })
  const closeVideoModal = () => setVideoModal({ display: false, video: null })

  return (
    <Container fluid>
      <Row>
        <Col>
          <h3 className="mb-4 font-weight-bold">{t("examples.title")}</h3>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={8}>
          <Card className="mb-4">
            <Card.Header>
              <Row>
                <Col xs={8}>
                  <h5 className="mb-0 mt-1">{destination && getLocaleValue(destination, "name", language)}</h5>
                </Col>
                <Col xs={4}>
                  <Nav
                    justify
                    defaultActiveKey={language}
                    onSelect={lng => setLanguage(lng ?? "en")}
                    className="language-navigation">
                    <Nav.Item>
                      <Nav.Link eventKey="fi">{t("common.languages.fi")}</Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="en">{t("common.languages.en")}</Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col>
                  {isLoading && (
                    <div className="loading-spinner">
                      <HiOutlineRefresh className="slow-spin icon loading-spinner-icon" />
                    </div>
                  )}
                  {reorderedGuides.map((data: Guide, index: number) => (
                    <Card
                      body
                      key={index}
                      className={"guide" + (data.id === selectedGuideId ? " selected" : "")}
                      onClick={() => setSelectedGuideId(data.id)}>
                      <IonIcon name={data.icon} style={undefined} />
                      <span className="ml-3">{getLocaleValue(data, "title", language)}</span>
                    </Card>
                  ))}
                  {!guides.length && <p className="text-center mt-3">{t("guides.noContent")}</p>}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={4}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">{t("guides.preview")}</h5>
            </Card.Header>
            <div className="mb-3">
              {blocks.map(({ data, type }, index) => {
                switch (type) {
                  case "header":
                  case "paragraph":
                    return <GuideTextPreview key={index} data={data as never} language={language} type={type} />
                  case "image":
                    return <GuideImage key={index} data={data as GuideImageData} list={false} preview full={false} />
                  case "video":
                    return <GuideVideoThumbnail key={index} data={data as VideoData} preview list onClick={openVideoModal} />
                  default:
                    return null
                }
              })}
              {!selectedGuide && (
                <p className="text-muted font-italic m-0 px-4 pt-3">{t("guides.selectedGuide.unselected")}</p>
              )}
              {selectedGuide && !blocks.length && (
                <p className="text-muted font-italic m-0 px-4 pt-3">{t("guides.selectedGuide.noContent")}</p>
              )}
            </div>
          </Card>
        </Col>
      </Row>
      <VideoModal video={videoModal.video} display={videoModal.display} close={closeVideoModal} />
    </Container>
  )
}

export default ExamplesContainer
